import React, { useState } from 'react';
import { X, Clock, DollarSign, AlertCircle, Send, CheckCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * OvertimeRequestModal
 *
 * Modal shown when contractor clocks out with hours exceeding the job estimate.
 * Allows contractor to request overtime payment with justification.
 */
export default function OvertimeRequestModal({
  overtimeInfo,
  onSubmit,
  onSkip,
  onClose,
  submitting = false
}) {
  const [justification, setJustification] = useState('');
  const [requestOvertime, setRequestOvertime] = useState(true);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      requestOvertime,
      justification: requestOvertime ? justification : null
    });
  };

  if (!overtimeInfo) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-background w-full max-w-lg rounded-xl overflow-hidden border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-primary/10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-text-primary">Additional Hours Worked</h2>
              <p className="text-orange-300 text-sm">You worked beyond the estimated time</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-text-secondary hover:text-text-primary hover:bg-surface rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Hours Summary */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-surface border border-border rounded-lg p-4">
              <p className="text-text-secondary text-xs mb-1">Estimated Hours</p>
              <p className="text-text-primary text-xl font-bold">{overtimeInfo.estimatedHours}h</p>
            </div>
            <div className="bg-surface border border-border rounded-lg p-4">
              <p className="text-text-secondary text-xs mb-1">Actual Hours</p>
              <p className="text-orange-400 text-xl font-bold">{overtimeInfo.actualHours}h</p>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="bg-surface border border-border rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-text-secondary mb-3">Payment Breakdown</h3>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-text-secondary">Base Pay (capped at {overtimeInfo.estimatedHours}h)</span>
                <span className="text-text-primary font-medium">${overtimeInfo.basePay.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-orange-400">
                  Overtime ({overtimeInfo.overtimeHours}h × ${overtimeInfo.hourlyRate})
                </span>
                <span className="text-orange-400 font-medium">
                  ${overtimeInfo.potentialOvertimePay.toFixed(2)}
                </span>
              </div>
              <div className="border-t border-border-light pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-text-secondary">Potential Total</span>
                  <span className="text-green-400 font-bold">
                    ${overtimeInfo.totalPotential.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Request Toggle */}
          <div className="mb-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={requestOvertime}
                onChange={(e) => setRequestOvertime(e.target.checked)}
                className="w-5 h-5 text-primary rounded border-border bg-surface focus:ring-primary focus:ring-offset-background"
              />
              <span className="text-text-primary">Request payment for overtime hours</span>
            </label>
          </div>

          {/* Justification */}
          {requestOvertime && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-text-secondary mb-2">
                Justification <span className="text-red-400">*</span>
              </label>
              <textarea
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                placeholder="Explain why additional time was needed..."
                rows={3}
                className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-primary resize-none"
                required
              />
              <p className="mt-2 text-xs text-text-secondary flex items-center gap-1">
                <AlertCircle size={12} />
                This will be reviewed by the company before approval.
              </p>
            </div>
          )}

          {!requestOvertime && (
            <div className="mb-6 p-4 bg-surface border border-border rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-text-primary font-medium">Accept Base Pay Only</p>
                  <p className="text-text-secondary text-sm mt-1">
                    You will receive ${overtimeInfo.basePay.toFixed(2)} for{' '}
                    {overtimeInfo.estimatedHours} hours. The additional hours will not be
                    compensated.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onSkip}
              disabled={submitting}
              className="px-4 py-2 bg-surface hover:bg-surface-light text-text-primary rounded-lg transition-colors disabled:opacity-50"
            >
              Skip & Submit
            </button>
            <button
              type="submit"
              disabled={(requestOvertime && !justification.trim()) || submitting}
              className={cn(
                'flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors',
                'bg-primary hover:bg-primary-600',
                'disabled:bg-primary/50 disabled:cursor-not-allowed'
              )}
            >
              <Send size={18} />
              {submitting
                ? 'Submitting...'
                : requestOvertime
                  ? 'Submit Request'
                  : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
